// src/modules/admin.js � Admin dashboard, approve/reject, usage, feedback tickets
import { state } from '../state.js';
import { getSessionToken, getAuthHeaders, apiFetch } from '../utils/api.js';
import { escapeHtml, showView, showToast, showConfirmDialog, navigateTo, setStatus } from '../utils/dom.js';
import { formatTimeAgo, formatDuration } from '../utils/format.js';
import { APP_VERSION, API_BASE, SESSION_ID } from '../utils/constants.js';
import { logger, apiFetchWithRefresh, redactForLogs } from './debug.js';
import { getCachedVisibleSections } from './auth.js';
import { showAnalytics, showProviderProfile } from './analytics.js';
import { initFeatureFlags } from './feature-flags.js';
import { ProfileStore } from '../stores/profile-store.js';

export function showAdmin() {
    // Guard: check cached visibleSections before navigating
    var sections = getCachedVisibleSections();
    if (sections && !sections.admin) {
        logger.warn('ADMIN', 'Navigation blocked — admin section hidden by visibleSections');
        return;
    }
    navigateTo('admin');
    location.hash = '#admin';
    loadAdminData();
}

export function hideAdmin() {
    navigateTo('record');
    if (location.hash === '#admin') {
        history.replaceState(null, '', location.pathname + location.search);
    }
}

// BUG-2 fix: handle hash-based navigation for page refresh and in-app hash changes
export function handleHashNavigation() {
    const hash = location.hash;
    if (hash === '#analytics') {
        showAnalytics();
    } else if (hash === '#profile') {
        showProviderProfile();
    } else if (hash === '#admin' && state.isAdmin) {
        showAdmin();
    } else if (hash === '#feature-flags' && state.isAdmin) {
        navigateTo('feature-flags');
        initFeatureFlags();
    }
}

export async function loadAdminData() {
    try {
        const pendingRes = await apiFetch('/admin/pending');
        const pendingData = await pendingRes.json();
        const pending = pendingData.pending || [];
        adminPendingCache = pending;
        document.getElementById('statPending').textContent = pending.length;

        const usersRes = await apiFetch('/admin/users');
        const usersData = await usersRes.json();
        const users = usersData.users || [];
        adminUsersCache = users;
        document.getElementById('statApproved').textContent = users.length;
        document.getElementById('statActiveUsers').textContent = usersData.activeCount || 0;

        // Load usage data (restore persisted filter prefs first)
        loadAdminFilterPrefs();
        loadAdminUsage();

        // Load feedback tickets
        loadAdminFeedback();

        // If modal is open, refresh its content
        var modal = document.getElementById('adminListModal');
        if (modal && modal.classList.contains('active')) {
            if (_adminModalType === 'pending' && _adminModalSubTab === 'rejected') {
                loadRejectedUsers();
            } else if (_adminModalType === 'pending') {
                renderAdminModalContent('pending');
            } else {
                renderAdminModalContent('approved');
            }
        }
    } catch (err) {
        logger.error('SYSTEM', 'Admin data load failed', err);
        showToast('Failed to load admin data. Try refreshing.', 'error');
    }
}

var _adminModalType = null; // tracks which modal is open: 'pending' or 'approved'
var _adminModalSubTab = 'pending'; // tracks sub-tab: 'pending' or 'rejected'
var adminRejectedCache = []; // cached rejected/revoked users

export function openAdminModal(type) {
    var modal = document.getElementById('adminListModal');
    if (!modal) return;
    _adminModalType = type;
    _adminModalSubTab = 'pending';
    var titleEl = document.getElementById('adminModalTitle');
    var tabsEl = document.getElementById('adminModalTabs');
    if (type === 'pending') {
        if (titleEl) titleEl.textContent = 'Pending Access Requests';
        if (tabsEl) tabsEl.classList.remove('d-none');
        updateAdminModalTabState();
    } else {
        if (titleEl) titleEl.textContent = 'Approved Users';
        if (tabsEl) tabsEl.classList.add('d-none');
    }
    renderAdminModalContent(type);
    modal.classList.add('active');
}

export function switchAdminModalTab(tab) {
    _adminModalSubTab = tab;
    updateAdminModalTabState();
    if (tab === 'rejected') {
        loadRejectedUsers();
    } else {
        renderAdminModalContent('pending');
    }
}

export function updateAdminModalTabState() {
    var pendingTab = document.getElementById('adminModalTabPending');
    var rejectedTab = document.getElementById('adminModalTabRejected');
    if (pendingTab) pendingTab.classList.toggle('active', _adminModalSubTab === 'pending');
    if (rejectedTab) rejectedTab.classList.toggle('active', _adminModalSubTab === 'rejected');
}

export async function loadRejectedUsers() {
    var container = document.getElementById('adminModalContent');
    if (!container) return;
    container.innerHTML = '<div class="empty-state"><p>Loading...</p></div>';
    try {
        var res = await apiFetch('/admin/pending?status=rejected');
        var data = await res.json();
        adminRejectedCache = data.pending || [];
        renderRejectedContent();
    } catch (err) {
        container.innerHTML = '<div class="empty-state"><p>Failed to load rejected users.</p></div>';
        logger.error('SYSTEM', 'Failed to load rejected users', err);
    }
}

export function renderRejectedContent() {
    var container = document.getElementById('adminModalContent');
    if (!container) return;
    var entries = adminRejectedCache;
    if (entries.length === 0) {
        container.innerHTML = '<div class="empty-state"><p>No rejected or revoked users.</p></div>';
        return;
    }
    container.innerHTML = entries.map(function(entry) {
        var statusClass = entry.status === 'revoked' ? 'badge-revoked' : 'badge-rejected';
        var statusLabel = entry.status === 'revoked' ? 'Revoked' : 'Rejected';
        var dateStr = entry.rejectedAt || entry.revokedAt || '';
        var dateDisplay = dateStr ? new Date(dateStr).toLocaleDateString() : '';
        return '<div class="waitlist-entry">' +
            '<div class="waitlist-info">' +
                '<div class="name">' + escapeHtml(entry.name || entry.email) +
                    ' <span class="admin-modal-status-badge ' + statusClass + '">' + statusLabel + '</span>' +
                '</div>' +
                '<div class="details">' +
                    escapeHtml(entry.email) + ' | ' + escapeHtml(entry.role || 'N/A') + ' | ' + escapeHtml(entry.clinicName || 'N/A') +
                    (dateDisplay ? '<br>' + statusLabel + ': ' + escapeHtml(dateDisplay) : '') +
                '</div>' +
            '</div>' +
            '<div class="waitlist-actions">' +
                '<button class="btn-approve" data-user-action="approve" data-user-email="' + escapeHtml(entry.email) + '">Approve</button>' +
            '</div>' +
        '</div>';
    }).join('');
    attachUserActionHandler(container);
}

export function closeAdminModal() {
    var modal = document.getElementById('adminListModal');
    if (modal) modal.classList.remove('active');
    _adminModalType = null;
    _adminModalSubTab = 'pending';
}

export function renderAdminModalContent(type) {
    var container = document.getElementById('adminModalContent');
    if (!container) return;

    if (type === 'pending') {
        var pending = adminPendingCache;
        if (pending.length === 0) {
            container.innerHTML = '<div class="empty-state"><p>No pending requests.</p></div>';
        } else {
            container.innerHTML = pending.map(function(entry) { return '<div class="waitlist-entry">' +
                '<div class="waitlist-info">' +
                    '<div class="name">' + escapeHtml(entry.name || entry.email) + '</div>' +
                    '<div class="details">' +
                        escapeHtml(entry.email) + ' | ' + escapeHtml(entry.role || 'N/A') + ' | ' + escapeHtml(entry.clinicName || 'N/A') +
                        (entry.phone ? ' | ' + escapeHtml(entry.phone) : '') +
                        (entry.referral ? '<br>Referral: ' + escapeHtml(entry.referral) : '') +
                        '<br>Submitted: ' + escapeHtml(new Date(entry.submittedAt).toLocaleDateString()) +
                    '</div>' +
                '</div>' +
                '<div class="waitlist-actions">' +
                    '<button class="btn-approve" data-user-action="approve" data-user-email="' + escapeHtml(entry.email) + '">Approve</button>' +
                    '<button class="btn-reject" data-user-action="reject" data-user-email="' + escapeHtml(entry.email) + '">Reject</button>' +
                '</div>' +
            '</div>'; }).join('');
            attachUserActionHandler(container);
        }
    } else {
        var users = adminUsersCache;
        if (users.length === 0) {
            container.innerHTML = '<div class="empty-state"><p>No approved users yet.</p></div>';
        } else {
            container.innerHTML = users.map(function(u) {
                var lastSeenHtml = '';
                if (u.isActive) {
                    lastSeenHtml = '<br><span class="text-muted small">Last seen just now</span>';
                } else if (u.lastSeenAt) {
                    lastSeenHtml = '<br><span class="text-muted small">Last seen ' + escapeHtml(formatTimeAgo(u.lastSeenAt)) + '</span>';
                }
                var testBadgeHtml = u.userType === 'test'
                    ? ' <span class="badge-user-test">Test</span>'
                    : '';
                var toggleBtnHtml = '';
                var subBtnHtml = '';
                var trialDateHtml = '';
                if (!u.isAdmin) {
                    if (u.userType === 'test') {
                        toggleBtnHtml = '<button class="btn-approve btn-sm" data-user-action="toggle-user-type" data-user-email="' + escapeHtml(u.email) + '" data-user-type="test">Mark Real</button>';
                    } else {
                        toggleBtnHtml = '<button class="btn-outline-secondary btn-sm" data-user-action="toggle-user-type" data-user-email="' + escapeHtml(u.email) + '" data-user-type="real">Mark Test</button>';
                    }
                    var subStatus = u.subscriptionStatus || 'trial';
                    if (subStatus === 'subscribed') {
                        subBtnHtml = '<button class="btn-outline-secondary btn-sm" data-user-action="toggle-subscription" data-user-email="' + escapeHtml(u.email) + '" data-sub-status="subscribed">Mark Trial</button>';
                    } else {
                        subBtnHtml = '<button class="btn-approve btn-sm" data-user-action="toggle-subscription" data-user-email="' + escapeHtml(u.email) + '" data-sub-status="trial">Mark Subscribed</button>';
                    }
                    var trialDatePart = u.trialEndDate ? escapeHtml(String(u.trialEndDate).slice(0, 10)) : '';
                    trialDateHtml = '<input type="date" class="form-control input-style-1" data-user-action="trial-date-input" data-user-email="' + escapeHtml(u.email) + '" value="' + trialDatePart + '">' +
                        '<button class="btn-outline-secondary btn-sm" data-user-action="save-trial-date" data-user-email="' + escapeHtml(u.email) + '">Save</button>';
                }
                return '<div class="waitlist-entry">' +
                '<div class="waitlist-info">' +
                    '<div class="name">' + escapeHtml(u.name || u.email) + ' ' +
                        (u.isAdmin ? '<span style="color:var(--primary);font-size:11px;font-weight:600">(Admin)</span>' : '') +
                        testBadgeHtml + ' ' +
                        (u.isActive ? '<span class="user-status-dot active" title="Online"></span>' : '') +
                    '</div>' +
                    '<div class="details">' +
                        escapeHtml(u.email) + (u.role ? ' | ' + escapeHtml(u.role) : '') + (u.clinicName ? ' | ' + escapeHtml(u.clinicName) : '') +
                        (u.approvedAt ? '<br>Approved: ' + escapeHtml(new Date(u.approvedAt).toLocaleDateString()) : '') +
                        lastSeenHtml +
                    '</div>' +
                '</div>' +
                '<div class="waitlist-actions">' +
                    toggleBtnHtml +
                    (subBtnHtml ? ' ' + subBtnHtml : '') +
                    (trialDateHtml ? ' ' + trialDateHtml : '') +
                    (u.isAdmin
                        ? ' <span style="color:var(--text-muted);font-size:12px">Admin</span>'
                        : ' <button class="btn-reject" data-user-action="revoke" data-user-email="' + escapeHtml(u.email) + '">Revoke</button>') +
                '</div>' +
            '</div>'; }).join('');
            attachUserActionHandler(container);
        }
    }
}

export async function refreshAdminDashboard() {
    var btn = document.querySelector('[data-action="refresh-admin-dashboard"]');
    if (btn) {
        btn.disabled = true;
        btn.innerHTML = '<i class="bi bi-arrow-clockwise spin"></i> Refreshing...';
    }
    try {
        await loadAdminData();
    } finally {
        if (btn) {
            btn.disabled = false;
            btn.innerHTML = '<i class="bi bi-arrow-clockwise"></i> Refresh';
        }
    }
}

export async function refreshAdminModalData() {
    var btn = document.getElementById('adminModalRefresh');
    if (btn) { btn.disabled = true; btn.innerHTML = '<i class="bi bi-arrow-clockwise spin"></i>'; }
    try {
        if (_adminModalType === 'pending') {
            if (_adminModalSubTab === 'rejected') {
                await loadRejectedUsers();
            } else {
                var res = await apiFetch('/admin/pending');
                var data = await res.json();
                adminPendingCache = data.pending || [];
                document.getElementById('statPending').textContent = adminPendingCache.length;
                renderAdminModalContent('pending');
            }
        } else {
            var res = await apiFetch('/admin/users');
            var data = await res.json();
            adminUsersCache = data.users || [];
            document.getElementById('statApproved').textContent = adminUsersCache.length;
            document.getElementById('statActiveUsers').textContent = data.activeCount || 0;
            renderAdminModalContent('approved');
        }
    } catch (err) {
        logger.error('SYSTEM', 'Admin modal refresh failed', err);
    } finally {
        if (btn) { btn.disabled = false; btn.innerHTML = '<i class="bi bi-arrow-clockwise"></i>'; }
    }
}

// FIX N5: one delegated handler for the admin user lists — no JS built from
// server-supplied email strings.
export function attachUserActionHandler(container) {
    if (!container) return;
    container.onclick = (event) => {
        const btn = event.target.closest('[data-user-action]');
        if (!btn || !container.contains(btn)) return;
        const email = btn.dataset.userEmail || '';
        if (!email) return;
        if (btn.dataset.userAction === 'approve') approveUser(email);
        else if (btn.dataset.userAction === 'reject') rejectUser(email);
        else if (btn.dataset.userAction === 'revoke') revokeUser(email);
        else if (btn.dataset.userAction === 'toggle-user-type') toggleUserType(email, btn.dataset.userType);
        else if (btn.dataset.userAction === 'toggle-subscription') setSubscription(email, btn.dataset.subStatus);
        else if (btn.dataset.userAction === 'save-trial-date') {
            var input = container.querySelector('input[data-user-action="trial-date-input"][data-user-email="' + (window.CSS && CSS.escape ? CSS.escape(email) : email) + '"]');
            saveTrialDate(email, input ? input.value : '');
        }
    };
}

export async function approveUser(email) {
    var confirmed = await showConfirmDialog('Approve User', 'Approve ' + email + '?', 'Approve', 'btn-success');
    if (!confirmed) return;
    try {
        await apiFetch('/admin/approve', {
            method: 'POST',
            body: JSON.stringify({ email }),
        });
        loadAdminData();
    } catch (err) {
        showToast('Error: ' + err.message, 'error');
    }
}

export async function rejectUser(email) {
    var confirmed = await showConfirmDialog('Reject User', 'Reject ' + email + '?', 'Reject', 'btn-danger');
    if (!confirmed) return;
    try {
        await apiFetch('/admin/reject', {
            method: 'POST',
            body: JSON.stringify({ email }),
        });
        loadAdminData();
    } catch (err) {
        showToast('Error: ' + err.message, 'error');
    }
}

export async function revokeUser(email) {
    var confirmed = await showConfirmDialog('Revoke Access', 'Revoke access for ' + email + '?', 'Revoke', 'btn-danger');
    if (!confirmed) return;
    try {
        await apiFetch('/admin/revoke', {
            method: 'POST',
            body: JSON.stringify({ email }),
        });
        loadAdminData();
    } catch (err) {
        showToast('Error: ' + err.message, 'error');
    }
}

export async function toggleUserType(email, currentType) {
    var newType = currentType === 'test' ? 'real' : 'test';
    var label = newType === 'test' ? 'Mark as test user' : 'Mark as real user';
    var confirmed = await showConfirmDialog('Change User Type', label + ': ' + email + '?', 'Confirm', 'btn-primary');
    if (!confirmed) return;
    try {
        await apiFetch('/admin/set-user-type', {
            method: 'POST',
            body: JSON.stringify({ email, userType: newType }),
        });
        showToast('User type updated to ' + newType, 'success');
        loadAdminData();
    } catch (err) {
        showToast('Error: ' + err.message, 'error');
    }
}

export async function setSubscription(email, currentStatus) {
    var newStatus = currentStatus === 'subscribed' ? 'trial' : 'subscribed';
    var label = newStatus === 'subscribed' ? 'Mark as subscribed' : 'Mark as trial';
    var confirmed = await showConfirmDialog('Change Subscription', label + ': ' + email + '?', 'Confirm', 'btn-primary');
    if (!confirmed) return;
    var payload = { email: email, subscriptionStatus: newStatus };
    if (newStatus === 'trial') {
        // Backend requires a valid trialEndDate for trial status; default to 30 days out.
        payload.trialEndDate = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10) + 'T23:59:59Z';
    }
    try {
        await apiFetch('/admin/set-trial', {
            method: 'POST',
            body: JSON.stringify(payload),
        });
        showToast('Subscription updated to ' + newStatus, 'success');
        loadAdminData();
    } catch (err) {
        showToast('Error: ' + err.message, 'error');
    }
}

export async function saveTrialDate(email, dateValue) {
    if (!dateValue) {
        showToast('Pick a trial end date first', 'error');
        return;
    }
    try {
        await apiFetch('/admin/set-trial', {
            method: 'POST',
            body: JSON.stringify({ email: email, subscriptionStatus: 'trial', trialEndDate: dateValue + 'T23:59:59Z' }),
        });
        showToast('Trial end date updated', 'success');
        loadAdminData();
    } catch (err) {
        showToast('Error: ' + err.message, 'error');
    }
}

// ============================================================================
// ADMIN FEEDBACK TICKETS
// ============================================================================

let adminFeedbackTickets = [];
let adminFeedbackFilter = 'all';
let adminUsersCache = []; // Cached /admin/users for display name lookups
let adminPendingCache = []; // Cached /admin/pending for modal rendering

export function formatUserDisplay(email, doctorName) {
    const user = adminUsersCache.find(u => u.email === email);
    const clinic = (user && user.clinicName) ? user.clinicName : '';
    const name = doctorName || (user && user.name ? user.name : '');
    const prefixed = (name && !/^dr[.\s]/i.test(name)) ? 'Dr ' + name : name;
    if (prefixed && clinic) return prefixed + ' \u2014 ' + clinic;
    if (prefixed) return prefixed;
    if (clinic) return clinic;
    return email || 'Unknown';
}

export async function loadAdminFeedback() {
    try {
        const res = await apiFetch('/admin/feedback');
        const data = await res.json();
        adminFeedbackTickets = data.tickets || [];
        document.getElementById('feedbackTicketCount').textContent = adminFeedbackTickets.length;
        renderAdminFeedbackTickets();
    } catch (err) {
        logger.error('ADMIN', 'Failed to load admin feedback', err);
        document.getElementById('adminFeedbackList').innerHTML = '<div class="empty-state"><p>Failed to load feedback tickets.</p></div>';
    }
}

export function filterFeedbackTickets(filter) {
    adminFeedbackFilter = filter;
    // Update active button
    document.querySelectorAll('#feedbackFilters .filter-btn').forEach(btn => {
        btn.classList.toggle('active', btn.dataset.filter === filter);
    });
    renderAdminFeedbackTickets();
}

export function renderAdminFeedbackTickets() {
    const container = document.getElementById('adminFeedbackList');
    let filtered = adminFeedbackTickets;

    if (adminFeedbackFilter === 'bug') {
        filtered = filtered.filter(t => t.category === 'bug');
    } else if (adminFeedbackFilter === 'suggestion') {
        filtered = filtered.filter(t => t.category === 'suggestion' || t.category === 'feedback');
    } else if (adminFeedbackFilter === 'crash') {
        filtered = filtered.filter(t => t.category === 'crash');
    } else if (adminFeedbackFilter === 'status-open') {
        filtered = filtered.filter(t => t.status !== 'resolved');
    } else if (adminFeedbackFilter === 'status-resolved') {
        filtered = filtered.filter(t => t.status === 'resolved');
    }

    if (filtered.length === 0) {
        container.innerHTML = '<div class="empty-state"><p>No feedback tickets match this filter.</p></div>';
        return;
    }

    container.innerHTML = filtered.map(ticket => {
        const categoryColors = { bug: '#e74c3c', suggestion: '#3498db', feedback: '#3498db', crash: '#e67e22' };
        const statusColors = { submitted: '#95a5a6', acknowledged: '#f39c12', 'in-progress': '#3498db', resolved: '#27ae60' };
        const categoryColor = categoryColors[ticket.category] || '#95a5a6';
        const statusColor = statusColors[ticket.status] || '#95a5a6';
        const submittedDate = ticket.submittedAt ? new Date(ticket.submittedAt).toLocaleString() : 'Unknown';

        return `
            <div class="feedback-ticket-card" id="ticket-${ticket.ticketId}">
                <div class="feedback-ticket-header">
                    <div class="feedback-ticket-meta">
                        <span class="feedback-user-email">${escapeHtml(formatUserDisplay(ticket.userEmail, (ticket.diagnostics && ticket.diagnostics.profileName) || ticket.userName))}</span>
                        <span class="feedback-category-badge" style="background:${categoryColor}">${ticket.category}</span>
                        <span class="feedback-status-badge" style="background:${statusColor}">${ticket.status}</span>
                        ${ticket.screenshotCount > 0 ? `<span class="feedback-screenshot-indicator" data-action="view-ticket-screenshots" data-id="${ticket.ticketId}" data-stop>📷 ${ticket.screenshotCount} screenshot${ticket.screenshotCount > 1 ? 's' : ''}</span>` : ''}
                        <span class="feedback-screenshot-indicator" data-action="view-ticket-logs" data-id="${ticket.ticketId}" data-stop>📋 Logs</span>
                    </div>
                    <div class="feedback-ticket-date">${submittedDate}</div>
                </div>
                <div class="feedback-ticket-description">${escapeHtml(ticket.description || '')}</div>
                ${ticket.diagnostics && Object.keys(ticket.diagnostics).length > 0 ? `
                    <details class="feedback-diagnostics">
                        <summary>Diagnostics</summary>
                        <div class="feedback-diagnostics-content">
                            ${Object.entries(ticket.diagnostics).map(([k, v]) => {
                                if (v && typeof v === 'object') {
                                    return `<div><strong>${escapeHtml(k)}:</strong><pre class="diagnostics-json">${escapeHtml(JSON.stringify(v, null, 2))}</pre></div>`;
                                }
                                return `<div><strong>${escapeHtml(k)}:</strong> ${escapeHtml(String(v))}</div>`;
                            }).join('')}
                        </div>
                    </details>
                ` : ''}
                <div class="feedback-ticket-controls">
                    <div class="feedback-control-row">
                        <label>Status:</label>
                        <select id="status-${ticket.ticketId}" data-on-change="toggle-resolved-version" data-id="${ticket.ticketId}">
                            <option value="submitted" ${ticket.status === 'submitted' ? 'selected' : ''}>Submitted</option>
                            <option value="acknowledged" ${ticket.status === 'acknowledged' ? 'selected' : ''}>Acknowledged</option>
                            <option value="in-progress" ${ticket.status === 'in-progress' ? 'selected' : ''}>In Progress</option>
                            <option value="resolved" ${ticket.status === 'resolved' ? 'selected' : ''}>Resolved</option>
                        </select>
                    </div>
                    <div class="feedback-control-row feedback-resolved-version" id="resolved-row-${ticket.ticketId}" style="display:${ticket.status === 'resolved' ? 'flex' : 'none'}">
                        <label>Resolved in version:</label>
                        <input type="text" id="version-${ticket.ticketId}" placeholder="e.g. 2.9.0" value="${escapeHtml(ticket.resolvedInVersion || '')}" maxlength="20">
                    </div>
                    <div class="feedback-control-row">
                        <label>Admin note:</label>
                        <textarea id="note-${ticket.ticketId}" placeholder="Internal note or response to user..." rows="2">${escapeHtml(ticket.adminNote || '')}</textarea>
                    </div>
                    <button class="btn-update-ticket" data-action="update-feedback-ticket" data-id="${ticket.ticketId}" data-stop>Update</button>
                </div>
            </div>
        `;
    }).join('');
}

export function toggleResolvedVersion(ticketId) {
    const statusSelect = document.getElementById('status-' + ticketId);
    const resolvedRow = document.getElementById('resolved-row-' + ticketId);
    if (statusSelect && resolvedRow) {
        const isResolved = statusSelect.value === 'resolved';
        resolvedRow.style.display = isResolved ? 'flex' : 'none';
        // Auto-populate version with current APP_VERSION when marking resolved
        if (isResolved) {
            const versionInput = document.getElementById('version-' + ticketId);
            if (versionInput && !versionInput.value.trim()) {
                versionInput.value = APP_VERSION;
            }
        }
    }
}

export async function updateFeedbackTicket(ticketId) {
    const status = document.getElementById('status-' + ticketId)?.value || '';
    const resolvedInVersion = document.getElementById('version-' + ticketId)?.value || '';
    const adminNote = document.getElementById('note-' + ticketId)?.value || '';

    try {
        const res = await apiFetch('/admin/feedback/' + ticketId, {
            method: 'PATCH',
            body: JSON.stringify({ status, resolvedInVersion, adminNote }),
        });
        const data = await res.json();
        if (data.status === 'updated') {
            // Refresh the list
            loadAdminFeedback();
        } else {
            showToast('Failed to update ticket: ' + (data.error || 'Unknown error'), 'error');
        }
    } catch (err) {
        showToast('Error updating ticket: ' + err.message, 'error');
    }
}

export async function viewTicketScreenshots(ticketId) {
    try {
        const res = await apiFetch('/admin/feedback/' + ticketId + '/screenshots');
        if (!res.ok) {
            const errData = await res.json().catch(() => ({}));
            logger.error('ADMIN', 'Screenshot endpoint returned error', { status: res.status, error: errData.error });
            showToast('Error loading screenshots: ' + (errData.error || 'HTTP ' + res.status), 'error');
            return;
        }
        const data = await res.json();
        const screenshots = data.screenshots || [];

        if (screenshots.length === 0) {
            showToast('No screenshots available for this ticket.', 'info');
            return;
        }

        // Create a modal overlay to show screenshots with click-outside-to-close
        const overlay = document.createElement('div');
        overlay.className = 'modal-overlay screenshot-overlay active';
        overlay.id = 'screenshotModal';
        overlay.innerHTML = `
            <div class="modal screenshot-modal">
                <button class="screenshot-close-btn" aria-label="Close">&times;</button>
                <h2>Screenshots (${screenshots.length})</h2>
                <div class="screenshot-gallery">
                    ${screenshots.map((s, i) => `<div class="screenshot-item"><img src="${s.dataUrl}" alt="Screenshot ${s.index}" style="max-width:100%"></div>`).join('')}
                </div>
            </div>
        `;
        document.body.appendChild(overlay);

        logger.info('ADMIN', 'Screenshots loaded', { ticketId: ticketId.substring(0, 8), count: screenshots.length });

        // Close on clicking outside the modal content
        overlay.addEventListener('click', function(e) {
            if (e.target === overlay) {
                overlay.remove();
            }
        });
        // Close button handler
        overlay.querySelector('.screenshot-close-btn').addEventListener('click', function() {
            overlay.remove();
        });
        // Close on Escape key
        function handleEsc(e) {
            if (e.key === 'Escape') {
                overlay.remove();
                document.removeEventListener('keydown', handleEsc);
            }
        }
        document.addEventListener('keydown', handleEsc);
    } catch (err) {
        logger.error('ADMIN', 'Screenshot fetch failed', { error: err.message });
        showToast('Error loading screenshots: ' + err.message, 'error');
    }
}

export async function viewTicketLogs(ticketId) {
    try {
        const res = await apiFetch('/admin/feedback/' + ticketId + '/logs');
        const data = await res.json();

        if (!res.ok) {
            showToast(data.error || 'Failed to load logs for this ticket.', 'error');
            return;
        }

        const logsData = data.logs || {};
        const entries = logsData.entries || [];

        if (entries.length === 0) {
            showToast('No session logs available for this ticket.', 'info');
            return;
        }

        // Create modal overlay to show logs
        const overlay = document.createElement('div');
        overlay.className = 'modal-overlay';
        overlay.id = 'ticketLogsModal';
        overlay.style.display = 'flex';

        const filterCategories = ['ALL', 'ERRORS', 'UPLOAD', 'RECORDING', 'NETWORK', 'AUTH'];

        overlay.innerHTML = `
            <div class="modal" style="max-width:900px;max-height:90vh;overflow-y:auto">
                <button class="modal-close" data-action="remove-ticket-logs-modal">&times;</button>
                <h2>Session Logs (${entries.length} entries)</h2>
                <div class="logs-filters">
                    ${filterCategories.map(f => `<button class="${f === 'ALL' ? 'active' : ''}" data-filter="${f}" data-action="filter-ticket-logs">${f}</button>`).join('')}
                </div>
                <div class="ticket-logs" id="ticketLogsContainer">
                    ${renderLogEntries(entries, 'ALL')}
                </div>
            </div>
        `;
        overlay.dataset.entries = JSON.stringify(entries);
        document.body.appendChild(overlay);
    } catch (err) {
        showToast('Error loading logs: ' + err.message, 'error');
    }
}

export function filterTicketLogs(filter) {
    const modal = document.getElementById('ticketLogsModal');
    if (!modal) return;
    const entries = JSON.parse(modal.dataset.entries || '[]');
    const container = document.getElementById('ticketLogsContainer');
    if (container) {
        container.innerHTML = renderLogEntries(entries, filter);
    }
    // Update active button
    modal.querySelectorAll('.logs-filters button').forEach(btn => {
        btn.classList.toggle('active', btn.dataset.filter === filter);
    });
}

export function renderLogEntries(entries, filter) {
    let filtered = entries;
    if (filter === 'ERRORS') {
        filtered = entries.filter(e => e.level === 'ERROR');
    } else if (filter && filter !== 'ALL') {
        filtered = entries.filter(e => e.category === filter);
    }

    if (filtered.length === 0) {
        return '<div class="empty-state"><p>No log entries match this filter.</p></div>';
    }

    return filtered.map(entry => {
        const ts = entry.timestamp ? entry.timestamp.substring(11, 23) : '';
        const level = entry.level || 'INFO';
        const category = entry.category || '';
        const message = entry.message || '';
        const dataStr = entry.data ? JSON.stringify(entry.data, null, 2) : '';

        return `<div class="log-entry log-${level}">
            <span class="log-time">${escapeHtml(ts)}</span>
            <span class="log-level level-${level}">${level}</span>
            <span class="log-category">${escapeHtml(category)}</span>
            <span class="log-message">${escapeHtml(message)}</span>
            ${dataStr ? `<div class="log-data">${escapeHtml(dataStr)}</div>` : ''}
        </div>`;
    }).join('');
}

// ============================================================================

// ADMIN USAGE & ACTIVITY
// ============================================================================
export async function triggerBackfill() {
    const btn = document.getElementById('btnBackfill');
    const status = document.getElementById('backfillStatus');
    btn.disabled = true;
    btn.innerHTML = '<span class="backfill-spinner"></span> Running...';
    status.textContent = '';

    try {
        const res = await apiFetch('/admin/backfill-costs', { method: 'POST' });
        if (!res.ok) throw new Error('Backfill request failed');
        const data = await res.json();
        status.textContent = '\u2713 ' + data.message + ' (Total: $' + (data.totalCostCalculated || 0).toFixed(4) + ')';
        status.className = 'backfill-status success';
        btn.style.display = 'none';
        // Refresh usage data
        loadAdminUsage();
    } catch (err) {
        status.textContent = 'Error: ' + err.message;
        status.className = 'backfill-status error';
        btn.disabled = false;
        btn.innerHTML = '\u26a1 Backfill Past Data';
    }
}

// Admin usage state variables
let adminNotesPageSize = 25;
let adminNotesOffset = 0;
let adminNotesTotal = 0;
let adminDateFrom = '';
let adminDateTo = '';
let adminUsageLoading = false;
let adminOfficeFilter = '';
let adminProfileFilter = '';
// User visibility filter: 'all', 'real', 'test' — persisted in localStorage
let adminUserVisibility = localStorage.getItem('dentscribe_admin_visibility') || 'real';
// Excluded hashes for "test" users (super admin's own test accounts) — persisted
let adminTestHashes = JSON.parse(localStorage.getItem('dentscribe_admin_testHashes') || '[]');

export function saveAdminFilterPrefs() {
    localStorage.setItem('dentscribe_admin_visibility', adminUserVisibility);
    localStorage.setItem('dentscribe_admin_officeFilter', adminOfficeFilter);
    localStorage.setItem('dentscribe_admin_profileFilter', adminProfileFilter);
    localStorage.setItem('dentscribe_admin_dateFilter', document.getElementById('adminDateFilter')?.value || 'today-yesterday');
    localStorage.setItem('dentscribe_admin_testHashes', JSON.stringify(adminTestHashes));
}

export function loadAdminFilterPrefs() {
    adminUserVisibility = localStorage.getItem('dentscribe_admin_visibility') || 'real';
    adminOfficeFilter = localStorage.getItem('dentscribe_admin_officeFilter') || '';
    adminProfileFilter = localStorage.getItem('dentscribe_admin_profileFilter') || '';
    adminTestHashes = JSON.parse(localStorage.getItem('dentscribe_admin_testHashes') || '[]');
    const savedDate = localStorage.getItem('dentscribe_admin_dateFilter');
    if (savedDate) {
        const dateSel = document.getElementById('adminDateFilter');
        if (dateSel) dateSel.value = savedDate;
    }
    const visSel = document.getElementById('adminVisibilityFilter');
    if (visSel) visSel.value = adminUserVisibility;
}

export function getAdminDateRange() {
    const select = document.getElementById('adminDateFilter');
    if (!select) return { dateFrom: '', dateTo: '' };
    const val = select.value;
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    let dateFrom = '';
    let dateTo = '';
    if (val === 'today') {
        dateFrom = today.toISOString().split('T')[0];
        dateTo = dateFrom;
    } else if (val === 'yesterday') {
        const yd = new Date(today); yd.setDate(yd.getDate() - 1);
        dateFrom = yd.toISOString().split('T')[0];
        dateTo = dateFrom;
    } else if (val === 'today-yesterday') {
        const yd = new Date(today); yd.setDate(yd.getDate() - 1);
        dateFrom = yd.toISOString().split('T')[0];
        dateTo = today.toISOString().split('T')[0];
    } else if (val === 'week') {
        const wk = new Date(today); wk.setDate(wk.getDate() - 7);
        dateFrom = wk.toISOString().split('T')[0];
        dateTo = today.toISOString().split('T')[0];
    } else if (val === 'month') {
        const mo = new Date(today); mo.setDate(mo.getDate() - 30);
        dateFrom = mo.toISOString().split('T')[0];
        dateTo = today.toISOString().split('T')[0];
    }
    // 'all' returns empty strings — backend returns unfiltered
    return { dateFrom, dateTo };
}

export function onAdminOfficeFilterChange() {
    const sel = document.getElementById('adminOfficeFilter');
    const val = sel ? sel.value : '';
    // Value format: "office:HASH" or "profile:HASH:PROFILEID" or "" (all)
    if (val.startsWith('profile:')) {
        const parts = val.split(':');
        adminOfficeFilter = parts[1] || '';
        adminProfileFilter = parts.slice(2).join(':') || '';
    } else if (val.startsWith('office:')) {
        adminOfficeFilter = val.substring(7);
        adminProfileFilter = '';
    } else {
        adminOfficeFilter = '';
        adminProfileFilter = '';
    }
    adminNotesOffset = 0;
    saveAdminFilterPrefs();
    loadAdminUsage();
}

export function onAdminVisibilityChange() {
    const sel = document.getElementById('adminVisibilityFilter');
    adminUserVisibility = sel ? sel.value : 'all';
    adminNotesOffset = 0;
    saveAdminFilterPrefs();
    loadAdminUsage();
}

export function onAdminDateFilterChange() {
    adminNotesOffset = 0;
    saveAdminFilterPrefs();
    loadAdminUsage();
}

export function adminFilterByCard(name) {
    // Match card name to an office in the dropdown by checking if the name contains the clinic name
    const sel = document.getElementById('adminOfficeFilter');
    if (!sel) return;
    // Try to find matching option by checking if office clinicName is part of the card name
    let matched = false;
    for (const opt of sel.options) {
        if (opt.value && opt.textContent && name.includes(opt.textContent.replace(/^★ All — /, ''))) {
            sel.value = opt.value;
            matched = true;
            break;
        }
    }
    // If matched by optgroup label, try matching the office option
    if (!matched) {
        for (const group of sel.querySelectorAll('optgroup')) {
            if (name.includes(group.label)) {
                // Select the "All — OfficeName" option in this group
                const firstOpt = group.querySelector('option');
                if (firstOpt) {
                    sel.value = firstOpt.value;
                    matched = true;
                }
                break;
            }
        }
    }
    if (matched) {
        onAdminOfficeFilterChange();
    }
}

export function toggleTestAccount(hash) {
    const idx = adminTestHashes.indexOf(hash);
    if (idx >= 0) {
        adminTestHashes.splice(idx, 1);
    } else {
        adminTestHashes.push(hash);
    }
    saveAdminFilterPrefs();
    // If visibility is 'real', reload to exclude/include this hash
    if (adminUserVisibility === 'real') {
        adminNotesOffset = 0;
        loadAdminUsage();
    } else {
        // Just re-render cards without refetching
        loadAdminUsage();
    }
}

export function loadAdminNextPage() {
    if (adminNotesOffset + adminNotesPageSize < adminNotesTotal) {
        adminNotesOffset += adminNotesPageSize;
        loadAdminUsage();
    }
}

export function loadAdminPrevPage() {
    if (adminNotesOffset > 0) {
        adminNotesOffset = Math.max(0, adminNotesOffset - adminNotesPageSize);
        loadAdminUsage();
    }
}

export async function loadAdminUsage() {
    if (adminUsageLoading) return;
    adminUsageLoading = true;
    const wrapper = document.getElementById('usageTableWrapper');
    const paginationEl = document.getElementById('adminPagination');

    // Show loading skeleton
    wrapper.innerHTML = '<div class="admin-table-loading"><div class="admin-skeleton-row"></div><div class="admin-skeleton-row"></div><div class="admin-skeleton-row"></div><div class="admin-skeleton-row"></div><div class="admin-skeleton-row"></div></div>';
    if (paginationEl) paginationEl.innerHTML = '';

    try {
        const { dateFrom, dateTo } = getAdminDateRange();
        adminDateFrom = dateFrom;
        adminDateTo = dateTo;

        let url = '/admin/usage?limit=' + adminNotesPageSize + '&offset=' + adminNotesOffset;
        if (dateFrom) url += '&dateFrom=' + encodeURIComponent(dateFrom);
        if (dateTo) url += '&dateTo=' + encodeURIComponent(dateTo);
        if (adminOfficeFilter) url += '&office=' + encodeURIComponent(adminOfficeFilter);
        if (adminProfileFilter) url += '&profile=' + encodeURIComponent(adminProfileFilter);
        // Visibility filter: 'real' excludes test hashes, 'test' only shows test hashes
        if (adminUserVisibility === 'real' && adminTestHashes.length > 0) {
            url += '&exclude=' + encodeURIComponent(adminTestHashes.join(','));
        } else if (adminUserVisibility === 'test' && adminTestHashes.length > 0) {
            // When "Test Only", override office filter to show only test accounts
            // Use first test hash as office filter if no specific office set
            if (!adminOfficeFilter) {
                url += '&office=' + encodeURIComponent(adminTestHashes[0]);
            }
        }

        const res = await apiFetch(url);
        if (!res.ok) {
            wrapper.innerHTML = '<div class="empty-state"><p>Usage data not available.</p></div>';
            adminUsageLoading = false;
            return;
        }
        const data = await res.json();
        const notes = data.notes || [];
        const totals = data.totals || {};
        const byUser = data.byUser || {};
        const pagination = data.pagination || {};
        const offices = data.offices || [];
        const officeMonthlyAvg = data.officeMonthlyAvg || null;

        // Populate office filter dropdown with optgroups (offices + profiles)
        const officeSelect = document.getElementById('adminOfficeFilter');
        if (officeSelect && offices.length > 0) {
            // Determine which value to mark selected (from state vars, not DOM)
            const targetVal = adminProfileFilter
                ? 'profile:' + adminOfficeFilter + ':' + adminProfileFilter
                : (adminOfficeFilter ? 'office:' + adminOfficeFilter : '');
            let optionsHtml = '<option value=""' + (!targetVal ? ' selected' : '') + '>All Offices</option>';
            offices.forEach(o => {
                const officeVal = 'office:' + o.emailHash;
                const officeSelected = officeVal === targetVal ? ' selected' : '';
                const profiles = o.profiles || [];
                if (profiles.length > 0) {
                    optionsHtml += '<optgroup label="' + escapeHtml(o.clinicName) + '">';
                    optionsHtml += '<option value="' + escapeHtml(officeVal) + '"' + officeSelected + '>\u2605 All — ' + escapeHtml(o.clinicName) + '</option>';
                    profiles.forEach(p => {
                        const pVal = 'profile:' + o.emailHash + ':' + p.profileId;
                        const pSelected = pVal === targetVal ? ' selected' : '';
                        const drName = (p.displayName.toLowerCase().startsWith('dr ') || p.displayName.toLowerCase().startsWith('dr.'))
                            ? p.displayName : 'Dr ' + p.displayName;
                        optionsHtml += '<option value="' + escapeHtml(pVal) + '"' + pSelected + '>' + escapeHtml(drName) + '</option>';
                    });
                    optionsHtml += '</optgroup>';
                } else {
                    optionsHtml += '<option value="' + escapeHtml(officeVal) + '"' + officeSelected + '>' + escapeHtml(o.clinicName) + '</option>';
                }
            });
            officeSelect.innerHTML = optionsHtml;
        }

        adminNotesTotal = pagination.total || notes.length;

        // Update the notes count badge
        const notesBadge = document.getElementById('adminNotesBadge');
        if (notesBadge) notesBadge.textContent = adminNotesTotal;

        // Update stat cards
        document.getElementById('statTotalNotes').textContent = totals.totalNotes || 0;
        const totalMinutes = totals.totalTimeSavedMinutes || 0;
        if (totalMinutes >= 60) {
            const hrs = Math.floor(totalMinutes / 60);
            const mins = Math.round(totalMinutes % 60);
            document.getElementById('statTimeSaved').textContent = hrs + ' hrs ' + mins + ' min';
        } else {
            document.getElementById('statTimeSaved').textContent = Math.round(totalMinutes) + ' min';
        }

        // Render per-office summary cards (grouped by office, profiles inside)
        const userSummaryContainer = document.getElementById('userSummaryGrid');
        if (offices.length > 0 && Object.keys(byUser).length > 0) {
            // Build office cards from offices list, matching byUser stats
            userSummaryContainer.innerHTML = offices.map(office => {
                const profiles = office.profiles || [];
                // Sum stats for this office from byUser keys that contain the clinic name
                let officeNotes = 0, officeTime = 0, officeCost = 0;
                const matchedProfiles = [];
                Object.keys(byUser).forEach(name => {
                    const u = byUser[name];
                    // Match: name contains this office's clinicName, or office has a profile whose name is in the key
                    const nameInOffice = name.includes(office.clinicName) ||
                        profiles.some(p => name.includes(p.displayName));
                    if (nameInOffice) {
                        officeNotes += u.noteCount || 0;
                        officeTime += u.totalTimeSaved || 0;
                        officeCost += u.totalCost || 0;
                        matchedProfiles.push({ name, ...u });
                    }
                });
                // If no matches found via name, try by hash (single-profile offices without explicit name)
                if (matchedProfiles.length === 0) {
                    const officeName = office.clinicName;
                    Object.keys(byUser).forEach(name => {
                        if (name === officeName || name.includes(officeName)) {
                            const u = byUser[name];
                            officeNotes += u.noteCount || 0;
                            officeTime += u.totalTimeSaved || 0;
                            officeCost += u.totalCost || 0;
                            matchedProfiles.push({ name, ...u });
                        }
                    });
                }
                if (officeNotes === 0) return ''; // Skip offices with no notes in this view

                const timeFmt = officeTime >= 60
                    ? Math.floor(officeTime / 60) + 'h ' + Math.round(officeTime % 60) + 'm'
                    : Math.round(officeTime) + ' min';
                const isTest = adminTestHashes.includes(office.emailHash);
                const testBtnLabel = isTest ? '✓ Test' : 'Mark Test';
                const testBtnClass = isTest ? 'badge bg-warning text-dark' : 'badge bg-light text-muted';

                // Profile list inside card
                let profilesHtml = '';
                if (profiles.length > 1) {
                    profilesHtml = '<div class="office-card-profiles">' +
                        profiles.map(p => {
                            const drName = (p.displayName.toLowerCase().startsWith('dr ') || p.displayName.toLowerCase().startsWith('dr.'))
                                ? p.displayName : 'Dr ' + p.displayName;
                            // Find this profile's stats from matchedProfiles
                            const pStats = matchedProfiles.find(m => m.name.includes(p.displayName));
                            const pNotes = pStats ? pStats.noteCount : 0;
                            return pNotes > 0 ? '<span class="office-profile-tag">' + escapeHtml(drName) + ' <small>(' + pNotes + ')</small></span>' : '';
                        }).filter(Boolean).join('') +
                    '</div>';
                }

                return '<div class="user-summary-card office-grouped-card' + (isTest ? ' test-account' : '') + '" data-action="admin-filter-card" data-name="' + escapeHtml(office.clinicName) + '" style="cursor:pointer">' +
                    '<div class="d-flex justify-content-between align-items-start">' +
                        '<div class="user-summary-name">' + escapeHtml(office.clinicName) + '</div>' +
                        '<span class="' + testBtnClass + '" style="cursor:pointer;font-size:10px" data-action="toggle-test-account" data-hash="' + escapeHtml(office.emailHash) + '">' + testBtnLabel + '</span>' +
                    '</div>' +
                    profilesHtml +
                    '<div class="user-summary-stats">' +
                        '<span class="user-stat">' + officeNotes + ' notes</span>' +
                        '<span class="user-stat time">' + timeFmt + '</span>' +
                        '<span class="user-stat cost">$' + officeCost.toFixed(2) + '</span>' +
                    '</div>' +
                '</div>';
            }).filter(Boolean).join('');
        } else if (Object.keys(byUser).length > 0) {
            // Fallback: no offices data, render byUser as before
            const excludedAdminUsers = ['infyloop', 'pckdds'];
            const userNames = Object.keys(byUser).filter(name => !excludedAdminUsers.includes(name.toLowerCase()));
            userSummaryContainer.innerHTML = userNames.map(name => {
                const u = byUser[name];
                const timeSaved = u.totalTimeSaved >= 60
                    ? Math.floor(u.totalTimeSaved / 60) + 'h ' + Math.round(u.totalTimeSaved % 60) + 'm'
                    : Math.round(u.totalTimeSaved) + ' min';
                return '<div class="user-summary-card">' +
                    '<div class="user-summary-name">' + escapeHtml(name) + '</div>' +
                    '<div class="user-summary-stats">' +
                        '<span class="user-stat">' + (u.noteCount || 0) + ' notes</span>' +
                        '<span class="user-stat time">' + timeSaved + '</span>' +
                    '</div>' +
                '</div>';
            }).join('');
        } else {
            userSummaryContainer.innerHTML = '';
        }

        // Render monthly average banner (only when office is filtered)
        let monthlyAvgEl = document.getElementById('adminMonthlyAvgBanner');
        if (!monthlyAvgEl) {
            const userSection = document.querySelector('.user-summary-section');
            if (userSection) {
                const banner = document.createElement('div');
                banner.id = 'adminMonthlyAvgBanner';
                userSection.after(banner);
                monthlyAvgEl = banner;
            }
        }
        if (monthlyAvgEl) {
            if (officeMonthlyAvg && (adminOfficeFilter || adminProfileFilter)) {
                const avg = officeMonthlyAvg;
                monthlyAvgEl.innerHTML = '<div class="alert alert-info d-flex align-items-center flex-wrap gap-2 py-2 px-3 mb-0">' +
                    '<i class="bi bi-bar-chart-line me-1"></i>' +
                    '<strong>Monthly Average:</strong> ' +
                    '<span class="badge bg-primary">' + (avg.avgNotesPerMonth || 0).toFixed(1) + ' notes/mo</span> ' +
                    '<span class="badge bg-success">$' + (avg.avgCostPerMonth || 0).toFixed(4) + '/mo</span> ' +
                    '<span class="badge bg-info text-dark">' + (avg.avgTimeSavedPerMonth || 0).toFixed(1) + ' min saved/mo</span> ' +
                    '<span class="badge bg-secondary">Active: ' + (avg.monthsActive || 0) + ' months</span>' +
                '</div>';
            } else {
                monthlyAvgEl.innerHTML = '';
            }
        }

        // Check if backfill is needed
        const needsBackfill = notes.some(n => (n.totalCost || 0) === 0);
        const backfillBtn = document.getElementById('btnBackfill');
        if (backfillBtn && !needsBackfill) {
            backfillBtn.style.display = 'none';
        }

        // Build usage table
        if (notes.length === 0) {
            wrapper.innerHTML = '<div class="empty-state"><div class="empty-state-icon">📋</div><p>No notes found for this date range.</p><p class="empty-state-hint">Try selecting a different date filter.</p></div>';
            if (paginationEl) paginationEl.innerHTML = '';
            adminUsageLoading = false;
            return;
        }

        let tableHtml = '<table class="usage-table"><thead><tr>' +
            '<th>Date</th><th>User</th><th>Patient Tag</th><th>Duration</th><th>Time Saved</th><th>Cost</th><th>Pipeline</th><th>Actions</th>' +
            '</tr></thead><tbody>';

        notes.forEach(note => {
            const date = note.createdAt ? new Date(note.createdAt).toLocaleDateString() : '-';
            const duration = note.audioDurationSeconds ? formatDuration(note.audioDurationSeconds) : '-';
            const timeSaved = note.estimatedTimeSavedMinutes ? note.estimatedTimeSavedMinutes.toFixed(1) + ' min' : '-';
            const costDisplay = note.totalCost != null ? '$' + Number(note.totalCost).toFixed(4) : '-';
            const userName = note.userName || 'Unknown';
            const pipelineCell = formatPipelineCell(Object.assign({}, note.uploadMetrics || {}, note.pipelineTimings || {}));

            tableHtml += '<tr>' +
                '<td>' + escapeHtml(date) + '</td>' +
                '<td class="user-cell">' + escapeHtml(userName) + '</td>' +
                '<td>' + escapeHtml(note.patientLabel || '-') + '</td>' +
                '<td>' + duration + '</td>' +
                '<td class="time-cell">' + timeSaved + '</td>' +
                '<td class="cost-cell">' + costDisplay + '</td>' +
                '<td class="pipeline-cell">' + pipelineCell + '</td>' +
                '<td><button class="btn btn-sm btn-outline-primary" data-action="view-note" data-id="' + escapeHtml(note.noteId) + '">\uD83D\uDC41 View</button></td>' +
            '</tr>';
        });

        tableHtml += '</tbody></table>';
        wrapper.innerHTML = tableHtml;

        // Render pagination controls
        if (paginationEl) {
            const currentPage = Math.floor(adminNotesOffset / adminNotesPageSize) + 1;
            const totalPages = Math.ceil(adminNotesTotal / adminNotesPageSize);
            const showingFrom = adminNotesOffset + 1;
            const showingTo = Math.min(adminNotesOffset + adminNotesPageSize, adminNotesTotal);

            let pgHtml = '<div class="admin-pagination-info">' +
                '<span class="pagination-count">Showing ' + showingFrom + '–' + showingTo + ' of ' + adminNotesTotal + ' notes</span>' +
                '</div>';
            pgHtml += '<div class="admin-pagination-controls">';
            pgHtml += '<button class="btn-page' + (adminNotesOffset === 0 ? ' disabled' : '') + '" data-action="load-admin-prev-page" ' + (adminNotesOffset === 0 ? 'disabled' : '') + '>\u2190 Prev</button>';
            pgHtml += '<span class="page-indicator">Page ' + currentPage + ' of ' + totalPages + '</span>';
            pgHtml += '<button class="btn-page' + (!pagination.hasMore ? ' disabled' : '') + '" data-action="load-admin-next-page" ' + (!pagination.hasMore ? 'disabled' : '') + '>Next \u2192</button>';
            pgHtml += '</div>';
            paginationEl.innerHTML = pgHtml;
        }
    } catch (err) {
        logger.error('SYSTEM', 'Error loading usage data', err);
        wrapper.innerHTML = '<div class="empty-state"><div class="empty-state-icon">\u26A0\uFE0F</div><p>Error loading usage data.</p><button class="btn-retry" data-action="load-admin-usage">\u21BB Retry</button></div>';
    }
    adminUsageLoading = false;
}

/**
 * Format pipeline timing data for admin table cell.
 * Shows a summary badge with tooltip-style detail on hover/click.
 * Fields available from uploadMetrics (frontend-measured):
 *   uploadDurationMs, recordingDurationMs, audioSizeBytes, fileSizeBytes,
 *   uploadMethod, connectionType, totalFlowDurationMs
 * Fields available from pipelineTimings (backend-measured, when aws-ai-notes adds them):
 *   transcribeDurationMs, bedrockDurationMs, totalProcessingMs
 */
export function formatPipelineCell(metrics) {
    if (!metrics) return '<span class="pipeline-none">\u2014</span>';

    const uploadMs = metrics.uploadDurationMs || 0;
    const recordMs = metrics.recordingDurationMs || 0;
    const totalFlow = metrics.totalFlowDurationMs || 0;
    const fileSize = metrics.fileSizeBytes || metrics.audioSizeBytes || 0;
    const method = metrics.uploadMethod || '?';
    const connection = metrics.connectionType || '?';
    // Backend timing (populated once aws-ai-notes adds pipeline timing to DynamoDB)
    const transcribeMs = metrics.transcribeDurationMs || 0;
    const bedrockMs = metrics.bedrockDurationMs || 0;
    const totalProcessMs = metrics.totalProcessingMs || 0;

    // Summary badge: show total processing time if available (the interesting metric),
    // otherwise fall back to upload time
    const uploadSec = (uploadMs / 1000).toFixed(1);
    const sizeMB = (fileSize / (1024 * 1024)).toFixed(1);

    // Build detail lines for the expandable breakdown
    let details = '';
    if (recordMs > 0) details += '\u23FA Rec: ' + (recordMs / 1000).toFixed(0) + 's\n';
    details += '\u2B06 Upload: ' + uploadSec + 's (' + sizeMB + ' MB, ' + method + ')\n';
    if (transcribeMs > 0) details += '\uD83D\uDCDD Transcribe: ' + (transcribeMs / 1000).toFixed(1) + 's\n';
    if (bedrockMs > 0) details += '\uD83E\uDDE0 Bedrock: ' + (bedrockMs / 1000).toFixed(1) + 's\n';
    if (totalProcessMs > 0) details += '\u2211 Processing: ' + (totalProcessMs / 1000).toFixed(1) + 's\n';
    if (totalFlow > 0) details += '\u23F1 Total flow: ' + (totalFlow / 1000).toFixed(1) + 's\n';
    details += '\uD83D\uDCF6 ' + connection;

    // Primary badge: total processing (backend end-to-end) if available, else upload time
    let badgeText;
    if (totalProcessMs > 0) {
        const totalSec = totalProcessMs / 1000;
        badgeText = '\u23F1' + (totalSec >= 60 ? (totalSec / 60).toFixed(1) + 'm' : totalSec.toFixed(0) + 's');
    } else {
        badgeText = '\u2B06' + uploadSec + 's';
    }

    return '<span class="pipeline-badge" title="' + escapeHtml(details) + '">' +
        badgeText +
        (transcribeMs > 0 ? ' \u2192 \uD83D\uDCDD' + (transcribeMs / 1000).toFixed(0) + 's' : '') +
        (bedrockMs > 0 ? ' \u2192 \uD83E\uDDE0' + (bedrockMs / 1000).toFixed(0) + 's' : '') +
        '</span>';
}

// ============================================================================